import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate, useLocation, useParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import CategorySlider from "../components/categoryslider";
import { useCurrency } from "../context/CurrencyContext";

import { Image_URL, API_BASE_URL } from "../config";

// ---------------------------------------------------------------------------
// Helpers (pulled out of the component so they aren't re-created every render)
// ---------------------------------------------------------------------------

const safeJSONParse = (value, fallback) => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

// If `metal` already carries a karat (e.g. "18K Yellow Gold"), leave it alone.
// Only prefix "9K " when the caller passed a bare metal name like "Yellow Gold".
const normalizeMetalWithKt = (metal) => {
  if (!metal) return metal;
  if (/\d+\s*k\b/i.test(metal)) return metal.trim();
  if (/(gold|platinum|silver)/i.test(metal)) return `9K ${metal}`.trim();
  return metal;
};

const ALLOWED_METALS = [
  "white gold",
  "yellow gold",
  "rose gold",
  "silver(925)",
  "silver",
  "platinum(950)",
  "platinum",
  "platinum 950",
];

const ITEMS_PER_PAGE = 12;
const VISIBLE_PAGE_BUTTONS = 10;

// FIX: tolerant in-stock check — handles "Yes", "yes", " YES ", true, "true", 1.
// Used by BOTH the "In Stock" filter and the green "In-stock" badge so they
// can never disagree with each other.
const isInStock = (p) => {
  const v = String(p?.inStock ?? "").trim().toLowerCase();
  return v === "yes" || v === "true" || v === "1";
};

// FIX: friendly labels for the sort dropdown toggle (it used to show the raw
// option key like "instock" / "lowToHigh").
const SORT_LABELS = {
  default: "Default sorting",
  latest: "Latest",
  lowToHigh: "Low to High",
  highToLow: "High to Low",
  instock: "In Stock",
  wishlist: "My Wishlist",
};

// Same windowed pagination as StockMaster, but showing 10 page numbers instead of 5.
const buildPageNumbers = (currentPage, totalPages) => {
  const pages = [];
  let startPage = Math.max(1, currentPage - 4);
  let endPage = Math.min(totalPages, currentPage + 5);

  if (currentPage <= 5) endPage = Math.min(VISIBLE_PAGE_BUTTONS, totalPages);
  if (currentPage >= totalPages - 4) {
    startPage = Math.max(1, totalPages - VISIBLE_PAGE_BUTTONS + 1);
  }

  if (startPage > 1) {
    pages.push(1);
    if (startPage > 2) pages.push("...");
  }

  for (let i = startPage; i <= endPage; i++) pages.push(i);

  if (endPage < totalPages) {
    if (endPage < totalPages - 1) pages.push("...");
    pages.push(totalPages);
  }

  return pages;
};

const Product = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedCurrency } = useCurrency();

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const shapeFromURL = queryParams.get("shape");

  const [products, setProducts] = useState([]);
  const [selectedMaterial, setSelectedMaterial] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOption, setSortOption] = useState("default");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSubCategory, setSelectedSubCategory] = useState("");
  const [selectedShape, setSelectedShape] = useState("");
  const [selectedStyle, setSelectedStyle] = useState("");
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [shapes, setShapes] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [selectedStone, setSelectedStone] = useState("");
  const [selectedMetal, setSelectedMetal] = useState("");
  const [wishlist, setWishlist] = useState([]);
  const [activeView, setActiveView] = useState(
    localStorage.getItem("activeView") || "wholesale"
  );
  const [, setViewVersion] = useState(0);

  // Explicit flag: true when the current `products` array came from the
  // server-side filtered endpoint (GetProductListbyshape) rather than the
  // full list.
  const [isApiFiltered, setIsApiFiltered] = useState(false);

  const applyMarkup = useCallback((base) => {
    const view = localStorage.getItem("activeView") || "wholesale";
    if (view === "wholesale") return base;

    const storedPayload = safeJSONParse(localStorage.getItem("priceTable_seller"), {});
    const table = Array.isArray(storedPayload.data) ? storedPayload.data : [];
    if (!table.length) return base;

    const range = table.find((r) => {
      const from = parseFloat(r.from || 0);
      const to = r.to === null ? Infinity : parseFloat(r.to);
      return base >= from && base <= to;
    });
    if (!range) return base;

    // FIX: fall back to 1 (no-op multiplier), not 0, when markup is missing/falsy —
    // matches StockMaster's behavior and avoids silently showing $0 prices.
    const markupPercent = parseFloat(range.markup || 1);
    return base * markupPercent;
  }, []);

  // ---------------------------------------------------------------------
  // Cross-tab / cross-component "activeView" sync
  // ---------------------------------------------------------------------
  useEffect(() => {
    const handleStorageChange = () => {
      setActiveView(localStorage.getItem("activeView") || "wholesale");
      setViewVersion((v) => v + 1);
    };

    const handleViewChange = (e) => {
      setActiveView(e.detail || localStorage.getItem("activeView") || "wholesale");
      setViewVersion((v) => v + 1);
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("viewChange", handleViewChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("viewChange", handleViewChange);
    };
  }, []);

  // ---------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------

  const applyProductResponse = useCallback((data, preferredMetal) => {
    setProducts(data);

    const initialSelected = {};
    data.forEach((product) => {
      if (preferredMetal) {
        const matchedMetal = product.productRelatedData?.find(
          (item) => item.relatedItemKey.toLowerCase() === preferredMetal.toLowerCase()
        );
        if (matchedMetal) {
          initialSelected[product.id] = matchedMetal.relatedItemKey;
          return;
        }
      }

      const matchedYellowGold = product.productRelatedData?.find((item) =>
        item.relatedItemKey.toLowerCase().includes("yellow gold")
      );
      if (matchedYellowGold) {
        initialSelected[product.id] = matchedYellowGold.relatedItemKey;
        return;
      }

      const fallback = product.productRelatedData?.find((item) =>
        ALLOWED_METALS.includes(item.relatedItemKey.toLowerCase())
      );
      if (fallback) {
        initialSelected[product.id] = fallback.relatedItemKey;
      }
    });

    setSelectedMaterial(initialSelected);
    setCategories([...new Set(data.map((p) => p.categoryName))].map((name) => ({ id: name, name })));
    setSubCategories([...new Set(data.map((p) => p.subCategoryName))].map((name) => ({ id: name, name })));
    setShapes([...new Set(data.map((p) => p.shapeName))].map((name) => ({ id: name, name })));
  }, []);

  const fetchProductData = useCallback(
    async (signal) => {
      try {
        const params = new URLSearchParams(window.location.search);
        const urlCollectionId = params.get("collectionId");
        const numericCollectionId = urlCollectionId ? Number(urlCollectionId) : null;

        setLoadingImages(true);

        const url =
          numericCollectionId === 3
            ? `${API_BASE_URL}/Product/GetProductList?collectionId=${numericCollectionId}`
            : `${API_BASE_URL}/Product/GetProductList`;

        const response = await fetch(url, { signal });
        const result = await response.json();

        if (result.success && result.data) {
          applyProductResponse(result.data, null);
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error fetching product data:", error);
        }
      } finally {
        if (!signal?.aborted) setLoadingImages(false);
      }
    },
    [applyProductResponse]
  );

  const handleShapeFilter = useCallback(
    async (shape, stone, metal, category, style, signal) => {
      try {
        setLoadingImages(true);
        setIsApiFiltered(true);

        const params = new URLSearchParams();
        if (shape?.trim()) params.append("shapeName", shape.trim());
        if (stone?.trim()) params.append("stoneName", stone.trim());
        if (metal?.trim()) params.append("metalName", metal.trim());
        if (category?.trim()) params.append("categoryName", category.trim());
        if (style?.trim()) params.append("styleName", style.trim());

        const response = await fetch(
          `${API_BASE_URL}/Product/GetProductListbyshape?${params.toString()}`,
          { signal }
        );
        const result = await response.json();

        if (result.success && result.data) {
          applyProductResponse(result.data, metal);
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error fetching filtered product data:", error);
        }
      } finally {
        if (!signal?.aborted) setLoadingImages(false);
      }
    },
    [applyProductResponse]
  );

  // Sync state with URL query params, and (re)fetch — with abort protection
  // so a slow earlier request can't clobber a faster, more recent one.
  useEffect(() => {
    const controller = new AbortController();
    const params = new URLSearchParams(location.search);

    const category = params.get("category") || "";
    const shape = params.get("shape") || "";
    const stone = params.get("stone") || "";
    const metal = params.get("metal") || "";
    const style = params.get("style") || "";
    const subcategory = params.get("subcategory") || "";

    setSelectedCategory(category);
    setSelectedSubCategory(subcategory);
    setSelectedShape(shape);
    setSelectedStone(stone);
    setSelectedMetal(metal);
    setSelectedStyle(style);
    setCurrentPage(1);

    if (shape || stone || metal || category || style || subcategory) {
      const normalizedMetal = normalizeMetalWithKt(metal);
      handleShapeFilter(shape, stone, normalizedMetal, category, style, controller.signal);
    } else {
      setIsApiFiltered(false);
      fetchProductData(controller.signal);
    }

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // ---------------------------------------------------------------------
  // Wishlist
  // ---------------------------------------------------------------------
  useEffect(() => {
    const fetchWishlist = async () => {
      const storedUser = safeJSONParse(localStorage.getItem("user"), {});
      const userId = storedUser?.userId || 0;
      const companyId = storedUser?.companyId || 0;
      if (!companyId) return;

      try {
        const res = await fetch(`${API_BASE_URL}/Order/GetWishlist?userId=${userId}&CompanyId=${companyId}`);
        const data = await res.json();
        if (Array.isArray(data)) {
          setWishlist(
            data.map((item) => ({
              WishlistID: item.wishlistID,
              ProductID: item.productID,
              ProductCode: item.productCode,   // FIX: was item.productName
              MetalKT: item.metalName,
              Shape: item.shape,
            }))
          );
        }
      } catch (error) {
        console.error("Error fetching wishlist:", error);
      }
    };
    fetchWishlist();
  }, []);

  const [pendingWishlist, setPendingWishlist] = useState(new Set());

const toggleWishlist = useCallback(
  async (product) => {
    const productId = product.id;
    const shape = product.shapeName;
    const key = `${productId}_${shape}`;

    // FIX: ignore clicks while a request for this exact item is already in flight
    if (pendingWishlist.has(key)) return;
    setPendingWishlist((prev) => new Set(prev).add(key));

    const storedUser = safeJSONParse(localStorage.getItem("user"), {});
    const userId = storedUser?.userId || 0;
    const companyId = storedUser?.companyId || 0;
    const metalName = selectedMaterial[product.id];
    const productCode = product.productCode;

    try {
      const existing = wishlist.find(
        (w) => w.ProductID === productId && w.ProductCode === productCode && w.Shape === shape
      );

      if (existing?.WishlistID) {
        await fetch(
          `${API_BASE_URL}/Order/RemoveWishlist?WishlistID=${existing.WishlistID}&UserID=${userId}&CompanyId=${companyId}`,
          { method: "DELETE" }
        );
        setWishlist((prev) => prev.filter((w) => w.WishlistID !== existing.WishlistID));
        return;
      }

      const matchedImage = product.images?.find((img) =>
        img.value?.toLowerCase().includes(metalName?.toLowerCase())
      );
      const activeImage = matchedImage?.imageNames?.[0] || "";

      const payload = {
        UserID: userId,
        CompanyId: companyId,
        ProductID: productId,
        ProductCode: productCode,
        MetalKT: normalizeMetalWithKt(metalName),
        Shape: shape,
        ImageUrl: `${Image_URL}UatProduct/${activeImage}`,
      };

      const res = await fetch(`${API_BASE_URL}/Order/AddWishlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data?.success) {
        setWishlist((prev) => [
          ...prev,
          {
            WishlistID: data.wishlistID,
            ProductID: productId,
            ProductCode: productCode,
            MetalKT: normalizeMetalWithKt(metalName),
            Shape: shape,
          },
        ]);
      }
    } catch (error) {
      console.error("Error toggling wishlist item:", error);
    } finally {
      // FIX: release the lock once the request settles, success or failure
      setPendingWishlist((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  },
  [selectedMaterial, wishlist]
);
 
  // ---------------------------------------------------------------------
  // Derived data (memoized so it isn't recomputed on every unrelated render)
  // ---------------------------------------------------------------------

  const filteredProducts = useMemo(() => {
    const search = searchTerm.toLowerCase();

    return products.filter((p) => {
      const matchesName = p.productName?.toLowerCase().includes(search);
      const matchesMainCode = p.productCode?.toLowerCase().includes(search);
      const matchCodesArray = p.productCodesData?.some((item) =>
        item.productCode?.toLowerCase().includes(search)
      );
      const matchesSearch = matchesName || matchesMainCode || matchCodesArray;

      // If the server API already applied precise filters, only search-filter locally.
      if (isApiFiltered) {
        return matchesSearch;
      }

      const matchesCategory = selectedCategory
        ? String(p.categoryName || "").trim().toLowerCase() === selectedCategory.trim().toLowerCase()
        : true;

      const matchesSubCategory = selectedSubCategory
        ? String(p.subCategoryName || "").trim().toLowerCase() === selectedSubCategory.trim().toLowerCase()
        : true;

      const matchesShape = selectedShape
        ? p.shapeName
          ?.split(",")
          .map((s) => s.trim().toLowerCase())
          .some((s) => s === selectedShape.trim().toLowerCase())
        : true;

      return matchesSearch && matchesCategory && matchesSubCategory && matchesShape;
    });
  }, [products, searchTerm, isApiFiltered, selectedCategory, selectedSubCategory, selectedShape]);

  const getEffectivePrice = useCallback(
    (product) => {
      const selectedMetalKey = selectedMaterial[product.id];
      const metalObj = product.productRelatedData?.find((m) => m.relatedItemKey === selectedMetalKey);
      const basePrice = metalObj?.price ?? product.productPrice;
      return applyMarkup(basePrice * (selectedCurrency?.price ?? 1));
    },
    [selectedMaterial, applyMarkup, selectedCurrency]
  );

  const sortedProducts = useMemo(() => {
    return [...filteredProducts]
      .filter((p) => {
        // "My Wishlist" and "In Stock" are real FILTERS (they remove items).
        // FIX: "In Stock" used to be only a sort (in-stock items moved to the
        // top but out-of-stock ones stayed in the list), so it looked like
        // it wasn't working. It now behaves like "My Wishlist".
        if (sortOption === "wishlist") return wishlist.some((w) => w.ProductID === p.id);
        if (sortOption === "instock") return isInStock(p);
        return true;
      })
      .sort((a, b) => {
        if (sortOption === "latest") return new Date(b.createdOn) - new Date(a.createdOn);
        if (sortOption === "lowToHigh") return getEffectivePrice(a) - getEffectivePrice(b);
        if (sortOption === "highToLow") return getEffectivePrice(b) - getEffectivePrice(a);
        return 0;
      });
  }, [filteredProducts, sortOption, wishlist, getEffectivePrice]);

  const totalPages = Math.ceil(sortedProducts.length / ITEMS_PER_PAGE) || 1;

  const paginatedProducts = useMemo(
    () => sortedProducts.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE),
    [sortedProducts, currentPage]
  );

  const pageNumbers = useMemo(() => buildPageNumbers(currentPage, totalPages), [currentPage, totalPages]);

  // Reset to page 1 whenever the sort option changes, so a shorter
  // resulting list (e.g. switching to "My Wishlist" / "In Stock") can't
  // leave the user stranded on an out-of-range page with nothing rendered.
  useEffect(() => {
    setCurrentPage(1);
  }, [sortOption]);

  const handlePageChange = useCallback(
    (pageNumber) => {
      if (pageNumber > 0 && pageNumber <= totalPages) {
        setCurrentPage(pageNumber);
        window.scrollTo(0, 0);
      }
    },
    [totalPages]
  );

  if (!selectedCurrency) return null;

  return (
    <div className="home">
      <div id="page" className="hfeed page-wrapper">
        <Header />
        <div id="site-main" className="site-main">
          <div id="main-content" className="main-content">
            <div id="primary" className="content-area">
              <div id="content" className="site-content" role="main">
                <div className="">
                  <div className="section-container p-l-r">
                    <div className="row">
                      <div className="col-xl-12 col-lg-12 col-md-12 col-12">

                        <div className="products-topbar clearfix flex justify-between items-center" style={{ marginBottom: "10px" }}>
                          <div className="products-topbar-left flex items-center gap-4" style={{ marginTop: "30px" }}>
                            <div className="products-count container">
                              {/* FIX: use sortedProducts.length so the count matches what's
                                  actually paginated (e.g. correct in "My Wishlist" / "In Stock" view) */}
                              Showing {sortedProducts.length} of {products.length} results
                            </div>
                          </div>

                          <div style={{ display: "flex", whiteSpace: "nowrap", paddingBottom: "8px", marginTop: "30px" }}>
                            &nbsp;&nbsp;&nbsp;
                            <div className="products-topbar-right" style={{ marginLeft: "auto", display: "flex" }}>
                              <div style={{ position: "relative" }}>
                                <i
                                  className="icon-search"
                                  style={{
                                    position: "absolute",
                                    left: "12px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    color: "#6c757d",
                                    zIndex: 2,
                                    fontSize: "16px",
                                  }}
                                ></i>
                                <input
                                  type="text"
                                  placeholder="Search products..."
                                  value={searchTerm}
                                  onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setCurrentPage(1);
                                  }}
                                  className="border rounded-lg px-3 py-2 w-64 products-topbar-center flex"
                                  style={{
                                    paddingLeft: "40px",
                                    lineHeight: "25px",
                                    borderRadius: "5px",
                                    border: "none",
                                    boxShadow: "1px 1px 5px #ccc",
                                  }}
                                />
                              </div>
                              &nbsp; &nbsp;
                              <div className="products-sort dropdown">
                                <span className="sort-toggle dropdown-toggle" data-toggle="dropdown">
                                  {SORT_LABELS[sortOption] || SORT_LABELS.default}
                                </span>
                                <ul className="sort-list dropdown-menu">
                                  <li className={sortOption === "default" ? "active" : ""}>
                                    <a href="#0" onClick={() => setSortOption("default")}>Default sorting</a>
                                  </li>
                                  <li className={sortOption === "latest" ? "active" : ""}>
                                    <a href="#0" onClick={() => setSortOption("latest")}>Latest</a>
                                  </li>
                                  <li className={sortOption === "lowToHigh" ? "active" : ""}>
                                    <a href="#0" onClick={() => setSortOption("lowToHigh")}>Low to High</a>
                                  </li>
                                  <li className={sortOption === "highToLow" ? "active" : ""}>
                                    <a href="#0" onClick={() => setSortOption("highToLow")}>High to Low</a>
                                  </li>
                                  <li className={sortOption === "instock" ? "active" : ""}>
                                    <a href="#0" onClick={() => setSortOption("instock")}>In Stock</a>
                                  </li>
                                  <li className={sortOption === "wishlist" ? "active" : ""}>
                                    <a href="#0" onClick={() => setSortOption("wishlist")}>My Wishlist</a>
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Product Grid */}
                        <div className="tab-content">
                          <div className="tab-pane fade show active" id="layout-grid">
                            {loadingImages ? (
                              <div className="loading-spinner">
                                <div className="spinner"></div>
                              </div>
                            ) : paginatedProducts.length === 0 ? (
                              // FIX: empty-state message — previously an out-of-range page
                              // (or an empty wishlist) rendered nothing at all.
                              <p className="text-center text-muted w-100" style={{ padding: "40px 0" }}>
                                No products found.
                              </p>
                            ) : (
                              <div className="products-list grid">
                                <div className="row">
                                  {paginatedProducts.map((product) => {
                                    const selectedMetalKey = selectedMaterial[product.id];
                                    const selectedMetalObj = product.productRelatedData?.find(
                                      (item) => item.relatedItemKey.toLowerCase() === selectedMetalKey?.toLowerCase()
                                    );
                                    const metalBasePrice = selectedMetalObj?.price ?? product.productPrice;
                                    const materialId = selectedMaterial[product.id];
                                    const metalOptions = product.productRelatedData?.filter((item) =>
                                      ALLOWED_METALS.includes(item.relatedItemKey?.toLowerCase())
                                    );
                                    const selectedMetalOpt = metalOptions?.find(
                                      (item) => item.id === materialId || item.relatedItemKey === materialId
                                    );

                                    const metalName = selectedMetalOpt?.relatedItemKey || "";
                                    const matchedImage = product.images?.find((img) =>
                                      img.value?.toLowerCase()?.includes(metalName.trim().toLowerCase())
                                    );
                                    const imageArray = Array.isArray(matchedImage?.imageNames) ? matchedImage.imageNames : [];

                                    const shapeForWishlist = product.shapeName || "";
                                    const productCodeForWishlist = product.productCode || "";

                                    const isWishlisted = wishlist.some(
                                      (w) =>
                                        w.ProductID === product.id &&
                                        w.Shape === shapeForWishlist &&
                                        w.ProductCode === productCodeForWishlist
                                    );

                                    return (
                                      <div className="col-xl-3 col-lg-4 col-md-4 col-sm-6" key={product.id}>
                                        <div className="products-entry clearfix product-wapper">
                                          <div className="products-thumb" style={{ height: 350, width: 345 }}>
                                            {/* FIX: same isInStock() helper as the "In Stock" filter */}
                                            {isInStock(product) && (
                                              <div className="product-lable" id="in-stock">
                                                <div className="hot" style={{ backgroundColor: "#1b8f1d" }}>In-stock</div>
                                              </div>
                                            )}
                                            {product.bestseller === "Yes" && (
                                              <div className="product-lable">
                                                <div className="onsale">Best Seller</div>
                                              </div>
                                            )}
                                            <div className="product-thumb-hover">
                                              <Link
                                                to={`/productdetails/${product.id}?metal=${encodeURIComponent(
                                                  normalizeMetalWithKt(metalName)
                                                )}${shapeFromURL ? `&shape=${encodeURIComponent(shapeFromURL)}` : ""}`}
                                                rel="noopener noreferrer"
                                              >
                                                <img
                                                  height="600"
                                                  width="600"
                                                  src={`${Image_URL}UatProduct/${imageArray[0] || "default.png"}`}
                                                  className="post-image"
                                                  alt={product.productName}
                                                />
                                                <img
                                                  width="600"
                                                  height="600"
                                                  src={`${Image_URL}UatProduct/${imageArray[1] || imageArray[0] || "default.png"}`}
                                                  className="hover-image back"
                                                  alt={product.productName}
                                                />
                                              </Link>
                                            </div>
                                          </div>

                                          <div className="product-button" id="gold-div">
                                            <div className="stone-option-container">
                                              <div className="stone-options">
                                                {metalOptions?.map((item) => (
                                                  <button
                                                    key={`${product.id}-${item.relatedItemKey}`}
                                                    className={`stone-option-btn ${selectedMaterial[product.id] === item.relatedItemKey ? "active" : ""
                                                      }`}
                                                    onClick={() =>
                                                      setSelectedMaterial((prev) => ({ ...prev, [product.id]: item.relatedItemKey }))
                                                    }
                                                  >
                                                    <img
                                                      src={`https://westendjewellery.com/assets/img/${item.relatedItemKey
                                                        .toLowerCase()
                                                        .replace(/\s+/g, "-")}.png`}
                                                      width="20"
                                                      alt={item.relatedItemKey}
                                                    />
                                                  </button>
                                                ))}
                                                <div onClick={() => toggleWishlist(product)} style={{ cursor: "pointer" }}>
                                                  <i
                                                    className={`fa fa-heart ${isWishlisted ? "active" : ""}`}
                                                    style={{
                                                      fontSize: "20px",
                                                      padding: "10px",
                                                      color: isWishlisted ? "red" : "#ccc",
                                                      transition: "0.3s",
                                                    }}
                                                  />
                                                </div>
                                              </div>
                                              <p className="selected-stone-text">
                                                <span id="selected-stone">{product.productName} </span>{"  "}
                                                <span style={{ fontSize: 13 }} id="selected-stone">
                                                  {selectedCurrency?.name === "GBP" ? selectedCurrency?.name : `${selectedCurrency?.name} $`}{" "}
                                                  {applyMarkup(metalBasePrice * selectedCurrency.price).toFixed(0)}
                                                </span>
                                              </p>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Pagination Component — windowed like StockMaster, 10 buttons visible */}
                        {totalPages > 1 && (
                          <div className="pagination flex justify-center mt-10 mb-10">
                            <ul className="flex flex-wrap items-center gap-3">
                              <li>
                                <button
                                  className={`px-4 py-2 rounded-full pageBtns border transition-all duration-200 shadow-sm ${currentPage === 1
                                      ? "opacity-40 cursor-not-allowed bg-gray-100 text-gray-400"
                                      : "bg-white text-gray-700 hover:bg-gray-900 hover:text-white"
                                    }`}
                                  onClick={() => handlePageChange(currentPage - 1)}
                                  disabled={currentPage === 1}
                                >
                                  Prev
                                </button>
                              </li>

                              {pageNumbers.map((page, idx) =>
                                page === "..." ? (
                                  <li key={idx}>
                                    <span
                                      className="pageNum"
                                      style={{
                                        border: "none",
                                        background: "transparent",
                                        cursor: "default",
                                        padding: "8px 14px",
                                      }}
                                    >
                                      ...
                                    </span>
                                  </li>
                                ) : (
                                  <li key={idx}>
                                    <button
                                      className="px-4 py-2 rounded-full pageNum border transition-all duration-200 shadow-sm"
                                      style={{
                                        backgroundColor: currentPage === page ? "#7e7878" : "#ffffff",
                                        color: currentPage === page ? "#ffffff" : "#374151",
                                        borderColor: currentPage === page ? "#000000" : "#d1d5db",
                                      }}
                                      onClick={() => handlePageChange(page)}
                                    >
                                      {page}
                                    </button>
                                  </li>
                                )
                              )}

                              <li>
                                <button
                                  className={`px-4 py-2 rounded-full pageBtns border transition-all duration-200 shadow-sm ${currentPage === totalPages
                                      ? "opacity-40 cursor-not-allowed bg-gray-100 text-gray-400"
                                      : "bg-white text-gray-700 hover:bg-gray-900 hover:text-white"
                                    }`}
                                  onClick={() => handlePageChange(currentPage + 1)}
                                  disabled={currentPage === totalPages}
                                >
                                  Next
                                </button>
                              </li>
                            </ul>
                          </div>
                        )}

                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default Product;